import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// The MainHeader renders an empty <div> with this ID. Pages use <HeaderActions>
// to portal their page-specific buttons into that slot, so the global
// header stays a single component instead of knowing about every page.
// Static ID is fine because MainHeader mounts at most once at a time (the
// /map full-bleed route hides it, and no page uses HeaderActions from there).
export const PAGE_HEADER_SLOT_ID = 'page-header-slot';

/**
 * Portals its children into the MainHeader's action slot. Safe to use on
 * any page — renders nothing when the slot isn't in the DOM (e.g. first
 * paint before MainHeader has mounted, or the full-bleed /map route).
 *
 * Usage:
 *   <HeaderActions>
 *     <button className="btn-ghost" onClick={refresh}>
 *       <i className="fa-solid fa-arrows-rotate" /> Refresh
 *     </button>
 *   </HeaderActions>
 */
export function HeaderActions({ children }) {
  const [target, setTarget] = useState(() =>
    typeof document !== 'undefined'
      ? document.getElementById(PAGE_HEADER_SLOT_ID)
      : null
  );

  useEffect(() => {
    if (target) return;
    // MainHeader mounts in the same layout pass as the page, but the slot
    // may not exist yet on very first render. A microtask defer gives React
    // one commit to place the header, after which we can attach.
    const el = document.getElementById(PAGE_HEADER_SLOT_ID);
    if (el) setTarget(el);
  }, [target]);

  if (!target) return null;
  return createPortal(children, target);
}
